import * as React from "react";
import {InputGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {LcdIcon} from "../icon/LcdIcon";
import {Spinner} from "../spinner/Spinner";
import {ReactElement} from "react";
import {defineMessages, InjectedIntlProps, injectIntl} from "react-intl";

const MESSAGES = defineMessages({
  NONE: {
    id: "studio.property.edit.none",
    defaultMessage: "None"
  }
});

interface SelectEditProps {
  name?: string;
  value: string;
  options: string[];
  defaultValue?: string;
  type?: string;
  onChange: (newValue: string) => void | Promise<void>;
  onBlur?: React.FocusEventHandler<HTMLSelectElement>;
  onFocus?: React.FocusEventHandler<HTMLSelectElement>;
  onDrop?: React.DragEventHandler<HTMLSelectElement>;
  onDragStart?: React.DragEventHandler<HTMLSelectElement>;
  readOnly?: boolean;
}

interface SelectEditState {
  isMouseOver: boolean;
  isEditing: boolean;
  editingValue: string;
  submitState: "NOT_STARTED" | "SUBMITTING" | "ERROR"; //no explicit FINISHED state, the edit field just goes back to 'display' mode
  submitError: Error;
}

/**
 * A Select edit field that can be edited upon mouseover, just like a JIRA text field.
 */
class SelectEditComponent extends React.Component<SelectEditProps & InjectedIntlProps, SelectEditState> {

  _selectInput: HTMLSelectElement;

  constructor(props) {
    super(props);
    this.state = {
      isMouseOver: false,
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
    };
  }

  reset = (): void => {
    this.setState({
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
    });
  }

  handleMouseOver = (): void => {
    this.setState({isMouseOver: true});
  }

  handleMouseOut = (): void => {
    this.setState({isMouseOver: false});
  }

  handleEditButtonClick = (): void => {
    if (this._selectInput) {
      this._selectInput.focus();
    }
  }

  handleChange = (): void => {
    const newValue = this._selectInput.value;
    //remove the error state when changing the input after an error
    this.setState(prevState => ({
      editingValue: newValue, submitState: "ERROR" ? "NOT_STARTED" : prevState.submitState
    }));
  }

  handleInputFocus = (): void => {
    this.setState(prevState => ({
      isEditing: true,
      editingValue: prevState.submitState === "ERROR" ? prevState.editingValue : this.props.value
    }));
  }

  handleInputBlur = (blurEvent: React.FocusEvent<HTMLSelectElement>): void => {
    if (this.state.editingValue !== this.props.value && this.state.submitState !== "ERROR") {
      this.valueChanged(this.state.editingValue).then(this.reset);
    } else {
      this.reset();
    }
    if (this.props.onBlur) {
      this.props.onBlur(blurEvent);
    }
  }

  valueChanged = (newValue: string): Promise<void> => {
    const onChangeResult = this.props.onChange(newValue);
    if (onChangeResult instanceof Promise) {
      this.setState({submitState: "SUBMITTING"});

      return onChangeResult.catch((error) => {
        this.setState({submitState: "ERROR", submitError: error})
        this._selectInput.focus();

        throw error;
      });
    } else {
      return Promise.resolve();
    }
  }

  handleKeyPress = (event: React.KeyboardEvent<never>): void => {
    //submit on ENTER
    if (event.key === 'Enter' && this._selectInput) {
      this._selectInput.blur();
    }
  }

  shouldShowInput = (): boolean => {
    const {isMouseOver, isEditing} = this.state;
    return isMouseOver || isEditing;
  }

  escapeKeyPressedListener = (event: KeyboardEvent): void => {
    //cancel on ESC, unfortunately ESC keys are only dispatched on document
    if (event.key === 'Escape' && this._selectInput) {
      //revert editingValue to this.props.value -> no onChange fired
      this.setState({editingValue: this.props.value});
      this._selectInput.blur();
    }
  }

  renderInput = (): ReactElement => {
    if (this.shouldShowInput() && !this.props.readOnly) {
      const inputValue = this.state.isEditing ? this.state.editingValue : this.props.value;
      document.addEventListener("keyup", this.escapeKeyPressedListener);
      //use a 'vanilla' input iso a FormControl to allow more low-level handling of events (select/focus/blur methods)

      return <select
          className="form-control selectEditInput"
          ref={(ref) => {
            this._selectInput = ref;
          }}
          onBlur={this.handleInputBlur}
          onFocus={this.handleInputFocus}
          onChange={this.handleChange}
          onKeyPress={this.handleKeyPress}
          name={this.props.value}
          value={inputValue || ""}
      >
        {
          this.props.options.map((value) =>
              (
                  <option key={value} value={value}>{value}</option>
              )
          )
        }
      </select>;
    }
    this._selectInput = null;
    document.removeEventListener("keyup", this.escapeKeyPressedListener);
    return (<div className="SelectEditDisplay">
      {this.props.children || this.props.value || this.props.defaultValue ||
       this.props.intl.formatMessage(MESSAGES.NONE)}
    </div>);
  }

  renderAddon = (): ReactElement => {
    if (this.props.readOnly) {
      return null;
    }
    const {submitState, submitError} = this.state;
    switch (submitState) {
    case "NOT_STARTED":
      return this.shouldShowInput() ?
             <InputGroup.Addon onClick={this.handleEditButtonClick}>
               <LcdIcon icon="pencil"/>
             </InputGroup.Addon> : null;
    case "SUBMITTING":
      return <InputGroup.Addon><Spinner/></InputGroup.Addon>;
    case "ERROR": {
      const errorMessage = submitError.message;
      const tooltip = <Tooltip id={(this.props.name || "selectedit") + "-error-tooltip"}>{errorMessage}</Tooltip>;
      return (
          <InputGroup.Addon>
            <OverlayTrigger placement="top" overlay={tooltip} defaultOverlayShown={true}>
              <LcdIcon icon="alert"/>
            </OverlayTrigger>
          </InputGroup.Addon>
      );
    }
    }
  }

  render(): ReactElement {
    return (
        <InputGroup className="selectEditContainer"
                    onMouseOver={this.handleMouseOver}
                    onMouseOut={this.handleMouseOut}>
          {this.renderInput()}{this.renderAddon()}
        </InputGroup>
    );
  }

}

export const SelectEdit = injectIntl(SelectEditComponent);